import { CommonModule } from "@angular/common";
import { CUSTOM_ELEMENTS_SCHEMA, inject, Injector, NgModule, provideAppInitializer } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ActivatedRoute, Router } from "@angular/router";
import { catchError, EMPTY, finalize, Observable, switchMap, take } from "rxjs";

import { JWT_OPTIONS, JwtConfig, JwtModule } from "@auth0/angular-jwt";
import { FlexLayoutModule, FlexModule } from "@ngbracket/ngx-layout";

import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatToolbarModule } from "@angular/material/toolbar";

import { ForgeAutocompleteModule, ForgeDialogModule, ForgePopoverModule } from "@tylertech/forge-angular";

import "./tylerComponents";
import "./tylerIcons";

import { CommandsModule } from "@tylertech/commands-module";
import { IncodeModule } from "@tylertech/incode-module";
import { LgdCheckboxModule, LgdPasswordModule, LgdTextModule, LgdTypeAheadModule } from "@tylertech/lgd-reactive-forms";
import { getLoggerFor, LoggerModule, LogLevel } from "@tylertech/logging";
import { OdataModule } from "@tylertech/odata-module";

import * as components from "./components";
import * as directives from "./directives";
import * as services from "./services";

import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { CoreRoutingModule } from "./coreRoutingModule";
import { ErpProJwtConfig } from "./erpProJwtConfig";
import { trapAndPollGatewayTimeouts, logMonitoredApiCalls } from "./interceptors";

const coreModuleLog = getLoggerFor("CoreModule");

// ServiceExceptionHandlerFactory.$inject = ["$injector"];
// function ServiceExceptionHandlerFactory($injector: ng.auto.IInjectorService): ng.IExceptionHandlerService
// {
//     return ServiceExceptionHandler;
//     function ServiceExceptionHandler(exception: Error, cause: string)
//     {
//         console.error(exception, cause);
//         const $loggingService = $injector.get("$loggingService") as LoggingService;
//         $loggingService.logToServer(exception, cause);
//     }
// }

// configureGesture.$inject = ["$mdGestureProvider"];
// function configureGesture($mdGestureProvider): void
// {
//     $mdGestureProvider.skipClickHijack();
// }

function jwtOptionsFactory(): JwtConfig
{
    return new ErpProJwtConfig();
}

function redirectIfDoubleSlashes(): void
{
    if (location.pathname?.includes('//'))
    {
        // angular router cannot handle double slash
        location.href = location.pathname.replace(/\/\//g, '/') + location.search;
    }
}

/**
 * This function will replace the default serialization logic for Date object
 * Deserialization isn't affected
 */
function replaceDateToJSON(): void
{
    Date.prototype.toJSON = function (this: Date): string
    {
        // If it's an invalid date, return now
        if (isNaN(this as unknown as number))
        {
            return new Date()
                .toISOString();
        }

        // If it's midnight in local time, use unspecified format 2021-08-09T00:00:00 without time zone info,
        // instead of default ISO/UTC format 2021-08-09T05:00:00.000Z
        if (this.toTimeString().substring(0, 8) === "00:00:00")
        {
            // e.g. 2021-08-09T00:00:00
            return this.getFullYear()
                + "-"
                + ("0" + (this.getMonth() + 1)).slice(-2)
                + "-"
                + ("0" + this.getDate()).slice(-2)
                + "T00:00:00";
        }
        else
        {
            // e.g. 2021-08-09T06:00:00.000Z
            return this.toISOString();
        }
    };
}

replaceDateToJSON();

function coreAppInitializer(): void | Observable<unknown> | Promise<unknown>
{
    const router = inject(Router);
    const activatedRoute = inject(ActivatedRoute);
    const systemInfoService = inject(services.SystemInfoService);
    const securityService = inject(services.SecurityService);
    const accessLogHandlerService = inject(services.AccessLogHandlerService);
    const storageService = inject(services.StorageService);
    const walkMeService = inject(services.WalkMeService);
    const aiAssistantService = inject(services.aiAssistantService);
    redirectIfDoubleSlashes();
    storageService.ClearOldKeys();

    walkMeService.forceExpireWalkMeCookies();

    return systemInfoService.getSystemInfo()
        .pipe(
            take(1),
            switchMap((systemInfo: services.SystemInfoExtended) =>
            {
                coreModuleLog.information(`Initializing AccessLogHandlerService`);
                accessLogHandlerService.start(systemInfo.IsTest());

                coreModuleLog.information(`Initializing WalkMe`);
                walkMeService.initializeScript(systemInfo.Product);

                coreModuleLog.information(`Initializing SecurityService`);
                return securityService.initialize();
            }),
            catchError((error) =>
            {
                if (services.isHttpResponseError(error)
                    && "ExceptionMessage" in error.error
                    && error.error.ExceptionMessage === "Unable to connect to the remote server")
                {
                    const redirectParams = securityService.buildRedirectParams(activatedRoute.snapshot);

                    router.navigate([services.SecurityRoute.Login], { queryParams: redirectParams });
                }

                return EMPTY;
            }),
            finalize(() =>
            {
                if (securityService.isAuthenticated)
                    aiAssistantService.initializeScript(securityService.principle);
                coreModuleLog.information(`app initialized`);
            })
        );
}

const angularModules = [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    FlexModule,
    FlexLayoutModule,
    FormsModule
];

const angularMaterialModules = [
    MatAutocompleteModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSnackBarModule,
    MatToolbarModule,
];

const lgdModules = [
    CommandsModule,
    OdataModule,
    IncodeModule,
    LgdCheckboxModule,
    LgdPasswordModule,
    LgdTextModule,
    LgdTypeAheadModule,
];

const exportedComponents = [
    components.AppOutletComponent,
    components.OmniBarComponent,
    directives.ForgeAvatarColorDirective,
    directives.ForgeAppBarProfileButtonAvatarColorDirective
];

@NgModule({
    imports: [
        ...angularModules,
        ...angularMaterialModules,
        ...lgdModules,
        LoggerModule.forRoot({
            logLevel: LogLevel.DEBUG
        }),
        JwtModule.forRoot({
            jwtOptionsProvider: {
                provide: JWT_OPTIONS,
                useFactory: jwtOptionsFactory,
            },
        }),
        ForgeDialogModule,
        ForgePopoverModule,
        ForgeAutocompleteModule,
        CoreRoutingModule,
    ],
    declarations: [
        ...exportedComponents,
        components.AppMenuComponent,
        components.ContextMenuComponent,
        components.MigrateTidFormComponent,
        components.SecurityScreenComponent,
        components.ResetPasswordFormComponent,
        components.ProfileMenuComponent,
        components.OmniSearchComponent,
        components.ConnGroupActionComponent,
        components.LoginFormComponent,
        components.SignoutFormComponent,
        components.ForgotPasswordFormComponent,
        components.ChangePasswordFormComponent,
        components.PasswordExpirationDialogComponent,
        components.ApplicationLinkComponent,
        components.HelpMenuComponent,
        components.SupportChatComponent,
        components.ShortCutLegendComponent,
        components.ShortcutGroupComponent,
        components.NineBoxComponent,
        components.IsLeafDirective,
        components.TreeComponent,
        components.ConnGroupPopoverComponent,
        components.LoginDialogComponent,
        components.TidLoginCallbackComponent,
        components.EscalationChatComponent,
    ],
    exports: exportedComponents,
    providers: [
        provideAppInitializer(coreAppInitializer),
        provideHttpClient(
            withInterceptors([
                logMonitoredApiCalls,
                trapAndPollGatewayTimeouts,
            ]),
            withInterceptorsFromDi(),
        ),
        services.AccessLogHandlerService,
        services.ApiService,
        services.AppService,
        services.AuthorizeService,
        services.BrowserService,
        services.CachedRepositoryService,
        services.CacheHelperService,
        services.ChatService,
        services.DefaultEmailService,
        services.DispatchService,
        services.FeatureFlagHelperService,
        services.FileService,
        services.FormMetadataService,
        services.HelpService,
        services.KeyHandlerService,
        services.LoggingService,
        services.NavigationService,
        services.NavMenuService,
        services.NotificationService,
        services.OmniBarService,
        services.OmniSearchService,
        services.OpenIdService,
        services.ProcessStepperService,
        services.ReportFormService,
        services.RepositoryService,
        services.RouteInfoService,
        services.SecurityService,
        services.SetupViewService,
        services.StateReportingService,
        services.StorageService,
        services.SystemInfoService,
        services.ThemeService,
        services.ToastService,
        services.UrlService,
        services.UserProfileService,
        services.UserService,
        services.WalkMeService,
        services.aiAssistantService,
        services.CxService,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CoreModule
{
}
